import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui/spinner';
import { useSessionStore } from '@/stores/session';
import { buildLoginRoute, LOGGED_OUT_PATH, LOGIN_PATH } from '@/features/auth/effeta-login';
import { ProviderUnavailableScreen } from '@/features/auth/components/ProviderUnavailableScreen';
import { meetsRequirements, type PermissionRequirement } from '@/lib/permissions';
import { isDefaultLanding } from '@/lib/default-landing';
import { APP_MODULES, type AppModuleKey } from '@/config/modules';
import { useHomeTarget } from '@/hooks/use-home-target';

// Guards de ruta: pieza central del sistema de auth.
// `RequireAuth` decide si dejar pasar al árbol o mandar a /login.
// Los guards por permiso se montan DENTRO de un RequireAuth.

interface RequireAuthProps {
  children: ReactNode;
}

// Loader full-screen para el momento `isBooting` — evita flicker de "no auth"
// antes de que la app termine de evaluar si hay sesión recuperable.
const BootLoader = () => {
  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-bg flex h-screen w-screen items-center justify-center"
    >
      <Spinner size={32} label="Cargando sesión" />
    </div>
  );
};

export const RequireAuth = ({ children }: RequireAuthProps) => {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const isBooting = useSessionStore((s) => s.isBooting);
  const issue = useSessionStore((s) => s.issue);
  const location = useLocation();

  if (isBooting) {
    return <BootLoader />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Rol de Effetá que no es del PIM: pantalla "sin acceso" (la de `no_role`).
  // El código queda en la URL para que recargar muestre lo mismo.
  if (issue === 'no-access') {
    return <Navigate to={`${LOGIN_PATH}?error=no_role`} replace />;
  }

  // Salió (aquí o en otra pestaña): "Saliste del PIM", nunca la entrada.
  if (issue === 'logged-out') {
    return <Navigate to={LOGGED_OUT_PATH} replace />;
  }

  // Effetá no contestó: la sesión puede seguir abierta, así que no se manda a
  // entrar. El store reintenta solo.
  if (issue === 'provider-unavailable') {
    return <ProviderUnavailableScreen />;
  }

  // Sin sesión: pantalla de entrada recordando la ruta + query original.
  return <Navigate to={buildLoginRoute(`${location.pathname}${location.search}`)} replace />;
};

// Release 2026-08-24: guard por permission del catálogo activo.
//
// `requires`: AND entre items, OR dentro de un grupo. Misma evaluación que el
// sidebar (`src/lib/permissions.ts`), así el menú no ofrece rutas que acá
// terminan en /forbidden.
//
// Fallback retrocompat: si el user viene sin `permissions` (un `/auth/me`
// que no lo trajera), abrimos la ruta — el backend igual gatea con 403 si el
// user no tiene permission. `permissions: []` sí bloquea.
//
// Destino por defecto del login sin el permiso: va a `/` en vez de /forbidden
// (ver `@/lib/default-landing`). Se evalúa en cada render, así que también
// aplica cuando los permisos llegan después de montar la ruta.
interface RequirePermissionProps {
  requires: readonly PermissionRequirement[];
  children: ReactNode;
}

export const RequirePermission = ({ requires, children }: RequirePermissionProps) => {
  const user = useSessionStore((s) => s.user);
  const isBooting = useSessionStore((s) => s.isBooting);
  const location = useLocation();

  if (isBooting) {
    return <BootLoader />;
  }

  if (!user) {
    return <Navigate to="/forbidden" replace />;
  }

  if (!meetsRequirements(user.permissions, requires)) {
    return <Navigate to={isDefaultLanding(location.state) ? '/' : '/forbidden'} replace />;
  }

  return <>{children}</>;
};

// Guard de un módulo del registro (`src/config/modules.ts`). Mismo requisito
// que decide si el módulo aparece en el sidebar.
interface RequireModuleProps {
  module: AppModuleKey;
  children: ReactNode;
}

export const RequireModule = ({ module, children }: RequireModuleProps) => {
  return <RequirePermission requires={APP_MODULES[module].requires}>{children}</RequirePermission>;
};

// Guard de `/`. El dashboard se abre solo si tiene alguna tarjeta de acción
// para el user; si no, va directo a su primera sección del menú (regla en
// `useHomeTarget`). `replace`: el botón atrás no vuelve a `/` para rebotar
// otra vez. Se monta dentro de `RequireAuth`, así que ya hay sesión.
interface RequireDashboardProps {
  children: ReactNode;
}

export const RequireDashboard = ({ children }: RequireDashboardProps) => {
  const home = useHomeTarget();

  if (!home.hasDashboard) {
    return <Navigate to={home.path} replace />;
  }

  return <>{children}</>;
};
